import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <svg
          key={s}
          className={`w-4 h-4 ${
            s <= rating ? "text-yellow-400" : "text-gray-300"
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterRating, setFilterRating] = useState("all");
  const [expandedComments, setExpandedComments] = useState({});
  const [editingReview, setEditingReview] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState("");

  useEffect(() => {
    async function fetchReviews() {
      setLoading(true);
      const snap = await getDocs(collection(db, "reviews"));
      const reviewsData = [];

      for (const d of snap.docs) {
        const r = d.data();

        let reviewerFirstname = "";
        let reviewerLastname = "";
        let reviewerEmail = "";
        let targetFirstname = "";
        let targetLastname = "";
        let targetEmail = "";

        if (r.reviewerId && typeof r.reviewerId === "string") {
          const reviewerSnap = await getDoc(doc(db, "users", r.reviewerId));
          if (reviewerSnap.exists()) {
            const data = reviewerSnap.data();
            reviewerFirstname = data.firstname || "";
            reviewerLastname = data.lastname || "";
            reviewerEmail = data.email || "";
          }
        }

        if (r.targetUserId && typeof r.targetUserId === "string") {
          const targetSnap = await getDoc(doc(db, "users", r.targetUserId));
          if (targetSnap.exists()) {
            const data = targetSnap.data();
            targetFirstname = data.firstname || "";
            targetLastname = data.lastname || "";
            targetEmail = data.email || "";
          }
        }

        reviewsData.push({
          id: d.id,
          offerId: r.offerId,
          reviewerId: r.reviewerId,
          targetUserId: r.targetUserId,
          rating: r.rating || 0,
          comment: r.comment || "",
          isClientReview: r.isClientReview || false,
          createdAt: r.createdAt,
          reviewerFirstname,
          reviewerLastname,
          reviewerEmail,
          targetFirstname,
          targetLastname,
          targetEmail,
        });
      }

      // Trier par date de création (plus récent en premier)
      reviewsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setReviews(reviewsData);
      setLoading(false);
    }
    fetchReviews();
  }, []);

  const toggleComment = (id) => {
    setExpandedComments((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setEditRating(review.rating);
    setEditComment(review.comment);
  };

  const saveEdit = async () => {
    if (!editingReview) return;
    try {
      await updateDoc(doc(db, "reviews", editingReview.id), {
        rating: editRating,
        comment: editComment.trim(),
      });
      setReviews((prev) =>
        prev.map((r) =>
          r.id === editingReview.id
            ? { ...r, rating: editRating, comment: editComment.trim() }
            : r
        )
      );
      setEditingReview(null);

      const div = document.createElement("div");
      div.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 text-sm";
      div.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Avis modifié avec succès !
      `;
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 3000);
    } catch {
      alert("Erreur lors de la modification");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Confirmez-vous la suppression de cet avis ?")) return;
    try {
      await deleteDoc(doc(db, "reviews", id));
      setReviews((prev) => prev.filter((r) => r.id !== id));

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Avis supprimé avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch {
      alert("Erreur lors de la suppression");
    }
  };

  const averageRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : "0.0";

  const clientReviewsCount = reviews.filter((r) => r.isClientReview === true).length;
  const couturierReviewsCount = reviews.filter((r) => r.isClientReview !== true).length;

  const filteredReviews = reviews.filter((r) => {
    const typeMatch =
      filterType === "all" ||
      (filterType === "client" && r.isClientReview === true) ||
      (filterType === "couturier" && r.isClientReview !== true);

    let ratingMatch = true;
    if (filterRating === "low") ratingMatch = r.rating >= 1 && r.rating <= 2;
    else if (filterRating === "mid") ratingMatch = r.rating === 3;
    else if (filterRating === "high") ratingMatch = r.rating >= 4 && r.rating <= 5;

    return typeMatch && ratingMatch;
  });

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Edit Modal */}
          {editingReview && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditingReview(null)}>
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Modifier l'avis</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Note</label>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          onClick={() => setEditRating(s)}
                          className="focus:outline-none"
                        >
                          <svg
                            className={`w-7 h-7 transition-colors ${
                              s <= editRating ? "text-yellow-400" : "text-gray-300"
                            } hover:text-yellow-400`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                      <span className="text-sm text-gray-500 ml-2">{editRating}/5</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Commentaire</label>
                    <textarea
                      value={editComment}
                      onChange={(e) => setEditComment(e.target.value)}
                      rows={4}
                      maxLength={500}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right mt-0.5">{editComment.length}/500</p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                  <button
                    onClick={() => setEditingReview(null)}
                    className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={saveEdit}
                    className="px-4 py-2 text-xs font-medium text-white bg-[#801111] rounded-md hover:bg-[#6a0e0e]"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total avis</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{reviews.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Note moyenne</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{averageRating} / 5</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avis clients</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{clientReviewsCount}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avis couturiers</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{couturierReviewsCount}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 p-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option value="all">Tous les avis</option>
              <option value="client">Clients uniquement</option>
              <option value="couturier">Couturiers uniquement</option>
            </select>
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option value="all">Toutes les notes</option>
              <option value="low">1-2 étoiles</option>
              <option value="mid">3 étoiles</option>
              <option value="high">4-5 étoiles</option>
            </select>
            {(filterType !== "all" || filterRating !== "all") && (
              <button
                onClick={() => { setFilterType("all"); setFilterRating("all"); }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Réinitialiser
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filteredReviews.length} résultat(s)</span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredReviews.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun avis trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Auteur</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Destinataire</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Commentaire</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredReviews.map((r) => {
                      const commentTruncated = r.comment.length > 200 && !expandedComments[r.id];
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-gray-900 text-sm">
                              {r.reviewerFirstname || r.reviewerLastname
                                ? `${r.reviewerFirstname} ${r.reviewerLastname}`.trim()
                                : "Inconnu"}
                            </p>
                            {r.reviewerEmail && <p className="text-xs text-gray-400">{r.reviewerEmail}</p>}
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-gray-900 text-sm">
                              {r.targetFirstname || r.targetLastname
                                ? `${r.targetFirstname} ${r.targetLastname}`.trim()
                                : "Inconnu"}
                            </p>
                            {r.targetEmail && <p className="text-xs text-gray-400">{r.targetEmail}</p>}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              <StarRating rating={r.rating} />
                              <span className="text-xs text-gray-500">{r.rating}/5</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 max-w-xs">
                            <p className="text-xs text-gray-600">
                              {commentTruncated ? r.comment.slice(0, 200) + "..." : r.comment || "-"}
                            </p>
                            {r.comment.length > 200 && (
                              <button onClick={() => toggleComment(r.id)} className="text-xs text-[#801111] mt-0.5">
                                {expandedComments[r.id] ? "Voir moins" : "Voir plus"}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${r.isClientReview ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"}`}>
                              {r.isClientReview ? "Client" : "Couturier"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                            {r.createdAt && r.createdAt.toDate
                              ? moment(r.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                              : r.createdAt ? moment(r.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openEditModal(r)} className="text-xs font-medium text-[#801111] hover:text-[#601010]">
                                Modifier
                              </button>
                              <button onClick={() => deleteReview(r.id)} className="text-xs font-medium text-red-600 hover:text-red-800">
                                Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
